import type { ItemStatusWrapper } from 'components/users/flexlist/FlexList';
import { LOADING, READY } from 'components/users/flexlist/FlexList';
import type { HoursPerWeekData } from 'components/users/HoursLoggedPerWeekGraph';
import {
  USERS_DASHBOARD_INCLUDE_PLANNED_TIME,
  USERS_DASHBOARD_INCLUDE_TASKED_TIME,
} from 'constants/user';
import { difference, isNumber, uniq } from 'lodash-es';
import { DateTime } from 'luxon';
import { getUserCapacitiesEntities } from 'redux/modules/capacities';
import {
  getDemoDashboardHasMore,
  getDemoDashboardLoadedCount,
  getDemoDashboardRows,
  getDemoDashboardUserIdsToLoad,
  getDemoDashGraphXhrUiStatus,
  getDemoDashListXhrUiStatus,
  getDemoFilteredUserCapacityMap,
  getDemoFilteredUserChangedMap,
  getDemoGraphData,
  getDemoUserWeekDetailsModalUi,
} from 'redux/modules/users-page/demo/usersPage.demo.selectors';
import type {
  Filter,
  HoursPerWeekSummary,
  PeriodDropdownOption,
  TimeframeFilterMethodOption,
  TotalsByUser,
  UserSummaryView,
  UsersSummaryQuery,
  UserViewModel,
  WeekSummary,
  WeekSummaryWithRange,
} from 'redux/modules/users-page/users-dashboard/usersDashboard.types';
import {
  periodOptionsDict,
  timeframeFilterMethodOptionsDict,
} from 'redux/modules/users-page/users-dashboard/usersDashboard.types';
import type { XhrStatusUIState } from 'redux/reusable/xhrStatus';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import {
  getAccountFeature,
  getAccountFeatures,
  getAccountWeeklyCapacity,
  getCurrentAccount,
  getStartOfWeek,
} from 'redux/selectors/accounts';
import { getIsAppInitialized } from 'redux/selectors/global';
import {
  getTeamsAsDropdownOptions,
  getUserIdsByTeamId,
  NO_TEAM_ASSIGNMENT,
} from 'redux/selectors/teams';
import { getUserId, getUserPermissions, getUserProperties } from 'redux/selectors/user';
import {
  getDeletedUsers,
  getNonDeletedUserIds,
  getNonDeletedUsers,
  getUserEntities,
} from 'redux/selectors/users';
import { getHistoricCapacityByDate } from 'redux/selectors/utils/capacities';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { Features, StartOfWeek } from 'types/account';
import type { RawCapacity } from 'types/summaries';
import { SUMMARY_API_LIMIT } from 'types/summaries';
import type { Avatar, User } from 'types/user';
import type { UsersDashboardPreferences } from 'types/users-dashboard';
import { getLastWeeksRange } from 'utils/date-helper';
import { durationFromHours } from 'utils/durations';
import { fromISO } from 'utils/luxon-helpers';
import { naturalCompare } from 'utils/naturalSort';
import { keyBy, notNull } from 'utils/typesafe';

const readUsers = (state: IState) => state.usersPage.dashboard.users;
const readWeeksByUser = (state: IState) => state.usersPage.dashboard.weeksByUser;
const readTotalsByUser = (state: IState) => state.usersPage.dashboard.totalsByUser;
const readHasMoreUsers = (state: IState) => state.usersPage.dashboard.hasMore;
const readXhrStatusDashboard = (state: IState) => state.usersPage.dashboard.xhrStatus;
const readUsersDashboardFilter = (state: IState) => state.usersPage.dashboard.filter;
const readHasLoadedUsersForDropdown = (state: IState) =>
  state.usersPage.dashboard.hasLoadedUsersForDropdown;
const readGraphTotals = (state: IState) => state.usersPage.dashboard.graphTotals;

export const getDashboardPreferences = createSelector(
  [getUserProperties],
  (userProperties): UsersDashboardPreferences => {
    return {
      includePlannedTime: Boolean(userProperties[USERS_DASHBOARD_INCLUDE_PLANNED_TIME] ?? true),
      includeTaskedTime: Boolean(userProperties[USERS_DASHBOARD_INCLUDE_TASKED_TIME] ?? true),
    };
  }
);

export const getRealDashGraphXhrUiStatus = createSelector(
  [readXhrStatusDashboard],
  (xhrStatus): XhrStatusUIState => xhrStatusUIState(xhrStatus, 'graph')
);

export const getDashGraphXhrUiStatus = createSelector(
  [getDemoDashGraphXhrUiStatus, getRealDashGraphXhrUiStatus],
  (demo, real) => demo ?? real
);

export const getRealDashListXhrUiStatus = createSelector([readXhrStatusDashboard], xhrStatus =>
  xhrStatusUIState(xhrStatus, 'users')
);

export const getDashListXhrUiStatus = createSelector(
  [getDemoDashListXhrUiStatus, getRealDashListXhrUiStatus],
  (demo, real) => demo ?? real
);

export const getDashboardXhrStatus = readXhrStatusDashboard;

export const getUsersDashboardFilter = readUsersDashboardFilter;

export const readUserWeekDetailsModal = (state: IState) =>
  state.usersPage.dashboard.userWeekDetailsModal;

const getNonDeletedUsersMap = createSelector([getNonDeletedUsers], users => keyBy(users, 'id'));

const getRealUserWeekDetailsModalUi = createSelector(
  [readUserWeekDetailsModal, getNonDeletedUsersMap, readWeeksByUser],
  (
    userWeekDetailsModal,
    userEntities,
    weeksByUser
  ): { user: User; week: WeekSummary } | undefined => {
    if (!userWeekDetailsModal) {
      return undefined;
    }

    const myUser = userEntities[userWeekDetailsModal.userId];
    // A demo id (or any id with no hydrated weeks yet) has no entry here at
    // all, not just no matching week — fall back to an empty lookup rather
    // than reading a property off undefined.
    const userWeeks = weeksByUser[userWeekDetailsModal.userId] ?? {};
    const myWeek = Object.hasOwn(userWeeks, userWeekDetailsModal.weekKey)
      ? userWeeks[userWeekDetailsModal.weekKey]
      : {
          week: userWeekDetailsModal.weekKey,
          capacity: durationFromHours(0),
          duration: durationFromHours(0),
          billable_duration: durationFromHours(0),
          non_billable_duration: durationFromHours(0),
          total_duration: durationFromHours(0),
          planned_duration: durationFromHours(0),
          tasked_duration: durationFromHours(0),
        };

    return {
      user: myUser,
      week: myWeek,
    };
  }
);

export const getUserWeekDetailsModalUi = createSelector(
  [getDemoUserWeekDetailsModalUi, getRealUserWeekDetailsModalUi],
  (demo, real) => demo ?? real
);

// Returns undefined if no filter, or the array of user IDs if filtered
export const getFilteredUserIds = createSelector(
  [getUsersDashboardFilter],
  (filter): number[] | undefined => {
    if (!filter.userIds || filter.userIds.length === 0) return undefined;
    return filter.userIds;
  }
);

// Returns undefined if no filter, or the array of team IDs if filtered
export const getFilteredTeamIds = createSelector(
  [getUsersDashboardFilter],
  (filter): number[] | undefined => {
    if (!filter.teamIds || filter.teamIds.length === 0) return undefined;
    return filter.teamIds;
  }
);

export const getUserIdsFromSelectedTeamIds = createSelector(
  [getFilteredTeamIds, getUserIdsByTeamId, getNonDeletedUserIds],
  (teamIds, userIdsByTeamId, nonDeletedUserIds) => {
    // When no team filter, return all users
    if (!teamIds) {
      return nonDeletedUserIds;
    }

    // Handle the case where no team assignment is selected
    const selectedUserIds = uniq(
      teamIds.flatMap(teamId => userIdsByTeamId[teamId]).filter(notNull)
    );

    if (!teamIds.includes(NO_TEAM_ASSIGNMENT)) {
      return selectedUserIds;
    }

    const usersWithoutTeam = difference(
      nonDeletedUserIds,
      Object.values(userIdsByTeamId).flat().filter(notNull)
    );

    return selectedUserIds.concat(usersWithoutTeam);
  }
);

export const getSelectedUserIds = createSelector(
  [getFilteredUserIds, getUserIdsFromSelectedTeamIds],
  (filteredUserIds, userIdsFromSelectedTeamIds) => {
    // When no user filter, fall back to team-based filtering (or all users)
    if (!filteredUserIds) {
      return userIdsFromSelectedTeamIds;
    }
    if (filteredUserIds.length) {
      return filteredUserIds;
    }
    // If users are not filtered by ids, they could be filtered by teams
    return userIdsFromSelectedTeamIds;
  }
);

export const getRealGraphData = createSelector(
  [
    readGraphTotals,
    getUsersDashboardFilter,
    getStartOfWeek,
    getUserCapacitiesEntities,
    getAccountWeeklyCapacity,
    getUserEntities,
    getDashboardPreferences,
    getSelectedUserIds,
  ],
  (
    graphTotals,
    filter,
    accountStartOfWeek,
    userCapacityEntities,
    accountWeeklyCapacity,
    userEntities,
    dashboardPreferences,
    selectedUserIds
  ) => {
    if (!graphTotals || !graphTotals.weeks || !graphTotals.capacity) {
      return undefined;
    }
    const result: Array<HoursPerWeekData> = [];
    const keys = Object.keys(graphTotals.weeks);
    const range = getLastWeeksRange(filter.to, filter.period, accountStartOfWeek);
    let from = fromISO(range.since);
    let to = from.plus({ days: 6 });

    for (let i = 0; i < +filter.period; i++) {
      const week = `${from.toISODate()}-${to.toISODate()}`;
      const capacityForWeek = selectedUserIds
        // TODO: If we implement paged user fetching similar to the project fetching revamp, this
        // approach will no longer work.
        .filter(userId => userEntities[userId]?.weekly_capacity)
        .reduce((sum, userId) => {
          const userCapacity = userCapacityEntities?.[userId];
          const capacityForCurrentWeek =
            getCapacityFromDateRange(week, userCapacity)?.weekly_capacity ?? accountWeeklyCapacity;

          return sum + capacityForCurrentWeek * 60;
        }, 0);

      if (keys.indexOf(week) > -1) {
        const item: HoursPerWeekSummary = graphTotals.weeks[week];

        const plannedMinutes = dashboardPreferences.includePlannedTime
          ? item.planned_duration?.total_minutes || 0
          : 0;
        const taskedMinutes = dashboardPreferences.includeTaskedTime
          ? item.tasked_duration?.total_minutes || 0
          : 0;
        let estimatedStack = plannedMinutes + taskedMinutes;

        const overtimeStack =
          capacityForWeek > 0 ? Math.max(item.duration.total_minutes - capacityForWeek, 0) : 0;
        const overschedulingStack =
          capacityForWeek > 0
            ? Math.max(item.duration.total_minutes + estimatedStack - capacityForWeek, 0) -
              overtimeStack
            : 0;

        let billableStack = item.billable_duration.total_minutes;
        let nonBillableStack = item.non_billable_duration.total_minutes;

        const remainingCapacityStack = Math.max(
          capacityForWeek - item.duration.total_minutes - estimatedStack,
          0
        );

        if (overtimeStack > 0) {
          nonBillableStack = Math.max(nonBillableStack - overtimeStack, 0);

          if (nonBillableStack + billableStack > capacityForWeek) {
            const toSubtract = nonBillableStack + billableStack - capacityForWeek;
            billableStack = billableStack - toSubtract;
          }
        }
        if (overschedulingStack > 0) {
          estimatedStack = Math.max(estimatedStack - overschedulingStack, 0);
        }
        const loggedStack = billableStack + nonBillableStack;
        const temp: HoursPerWeekData = {
          weekStart: fromISO(item.since).toISODate(),
          weekEnd: fromISO(item.until).toISODate(),

          billable: item.billable_duration.total_minutes,
          nonBillable: item.non_billable_duration.total_minutes,
          planned: item.planned_duration?.total_minutes || 0,
          tasked: item.tasked_duration?.total_minutes || 0,
          total: item.duration.total_minutes, // total here means billable + nonBillable.
          capacity: capacityForWeek,
          loggedStack,
          remainingCapacityStack,
          overtimeStack,
          overschedulingStack,
          nonBillableStack,
          billableStack,
          plannedStack: estimatedStack,
          totalStack: remainingCapacityStack + overtimeStack + nonBillableStack + billableStack,
        };
        result.push(temp);
      } else {
        const temp: HoursPerWeekData = {
          weekStart: from.toISODate(),
          weekEnd: to.toISODate(),
          loggedStack: 0,
          billable: 0,
          nonBillable: 0,
          planned: 0,
          tasked: 0,
          plannedStack: 0,
          capacity: capacityForWeek,
          remainingCapacityStack: capacityForWeek,
          overtimeStack: 0,
          overschedulingStack: 0,
          total: 0,
          billableStack: 0,
          nonBillableStack: 0,
          totalStack: capacityForWeek,
        };
        result.push(temp);
      }
      from = from.plus({ days: 7 });
      to = to.plus({ days: 7 });
    }
    return result;
  }
);

export const getGraphData = createSelector(
  [getDemoGraphData, getRealGraphData],
  (demo, real) => demo ?? real
);

export const getHasMoreUsers = (state: IState) => readHasMoreUsers(state) || false;

const userSummaryFactory = (
  capacities: Array<RawCapacity> | undefined,
  filter: Filter,
  accountStartOfWeek: StartOfWeek,
  accountWeeklyCapacity: number,
  weeks?: { [weekKey: string]: WeekSummary },
  totals?: TotalsByUser
) => {
  const { period } = filter;
  const range = getLastWeeksRange(filter.to, period, accountStartOfWeek);
  let from = fromISO(range.since);
  let to = from.plus({ days: 6 });

  const tempByWeek: Array<WeekSummaryWithRange> = [];
  const weekKeys = weeks ? Object.keys(weeks) : [];

  for (let i = 0; i < period; i++) {
    const fromFormatted = from.toISODate();
    const toFormatted = to.toISODate();
    const wKey = `${fromFormatted}-${toFormatted}`;

    const capacityForWeek = capacities ? getCapacityFromDateRange(wKey, capacities) : null;

    if (weeks && weekKeys.indexOf(wKey) > -1) {
      const weekSummary: WeekSummaryWithRange = {
        ...weeks[wKey],
        from: fromFormatted,
        to: toFormatted,
      };
      if (capacityForWeek?.weekly_capacity != null) {
        weekSummary.capacity = durationFromHours(capacityForWeek?.weekly_capacity);
      }
      tempByWeek.push(weekSummary);
    } else {
      const emptyWeekSummary: WeekSummaryWithRange = {
        week: wKey,
        capacity: durationFromHours(
          capacityForWeek && isNumber(capacityForWeek.weekly_capacity)
            ? capacityForWeek.weekly_capacity
            : accountWeeklyCapacity
        ),
        duration: durationFromHours(0),
        billable_duration: durationFromHours(0),
        non_billable_duration: durationFromHours(0),
        total_duration: durationFromHours(0),
        planned_duration: durationFromHours(0),
        tasked_duration: durationFromHours(0),
        from: fromFormatted,
        to: toFormatted,
      };
      tempByWeek.push(emptyWeekSummary);
    }

    from = from.plus({ days: 7 });
    to = to.plus({ days: 7 });
  }

  const tempSummary: UserSummaryView = {
    totals: totals || {
      weekly_capacity: durationFromHours(0),
      duration: durationFromHours(0),
      billable_duration: durationFromHours(0),
      non_billable_duration: durationFromHours(0),
      total_duration: durationFromHours(0),
      planned_duration: durationFromHours(0),
      tasked_duration: durationFromHours(0),
      updated_at: DateTime.now().toISODate(),
    },
    updated_at: DateTime.now().toISODate(),
    byWeek: tempByWeek,
  };
  return tempSummary;
};

export const getWeeksByUser = readWeeksByUser;

export const getUserIdsWithWeekSummary = createSelector([readWeeksByUser], weeksByUser =>
  Object.keys(weeksByUser)
);

/**
 * The set of users to render in the dashboard list, sorted by name.
 *
 * - Sleeping/burning modes: the API returns only the matching subset, so we
 *   render exactly what came back (`readUsers`).
 * - Default view: render the FULL selected directory — all users, or the
 *   user/team-filtered set — so nobody is hidden while their week summary is
 *   still loading. Summaries then stream in by id in batches (see
 *   `getDashboardUserIdsToLoad`). This keeps the list in sync with the "Anyone"
 *   dropdown instead of dropping users when backend pagination stops early or
 *   orders differently from the client. (TT-5478)
 */
export const getDashboardDisplayUserIds = createSelector(
  [getUsersDashboardFilter, readUsers, getSelectedUserIds, getNonDeletedUsersMap],
  (filter, apiResultUserIds, selectedUserIds, usersMap) => {
    const ids = filter.mode ? apiResultUserIds : selectedUserIds;
    return [...ids]
      .filter(id => usersMap[id] !== undefined)
      .sort((a, b) => naturalCompare(usersMap[a]?.name ?? '', usersMap[b]?.name ?? ''));
  }
);

export interface UserForDashboard {
  name: string;
  email: string;
  role_id: number;
  hide_hourly_rate: boolean;
  hide_internal_hourly_rate: boolean;
  default_hour_rate: number;
  internal_hour_rate: number;
  admin: boolean;
  avatar: Avatar;
  deleted: boolean;
  weekly_capacity: number;
  external_id?: string;
}

// Right now this is practically a copy of the above just filtering out users with an external_id.
// Backend is coming with support for querying imported users directly, so the inner functionality
// of this will change...
export const getImportedUsersForUnassignedList = createSelector([getDeletedUsers], deleted => {
  const isImportedUser = (user: User) => !!user.external_id;

  const result: Array<ItemStatusWrapper<UserForDashboard>> = deleted
    .filter(isImportedUser)
    .map(
      ({
        name,
        email,
        role_id,
        hide_hourly_rate,
        default_hour_rate,
        internal_hour_rate,
        hide_internal_hourly_rate,
        admin,
        avatar,
        deleted,
        weekly_capacity,
        external_id,
      }) => {
        const wrappedUser: ItemStatusWrapper<UserForDashboard> = {
          status: READY,
          item: {
            name,
            email,
            role_id,
            hide_hourly_rate,
            default_hour_rate,
            internal_hour_rate,
            hide_internal_hourly_rate,
            admin,
            avatar,
            deleted,
            weekly_capacity,
            external_id,
          },
        };
        return wrappedUser;
      }
    );
  return result;
});

export const getRealDashboardUsersWithWeekSummary = createSelector(
  [
    getDashboardDisplayUserIds,
    getNonDeletedUsersMap,
    readWeeksByUser,
    readTotalsByUser,
    getStartOfWeek,
    getUsersDashboardFilter,
    getUserIdsWithWeekSummary,
    getUserCapacitiesEntities,
    getAccountWeeklyCapacity,
  ],
  (
    ids,
    nonDeletedUsersMap,
    weeksByUser,
    totalsByUser,
    accountStartOfWeek: StartOfWeek,
    filter: Filter,
    userIdsWithWeekSummary,
    userCapacityEntities,
    accountWeeklyCapacity
  ) => {
    const isVirtualizedList = !filter.mode;
    // O(1) membership instead of Array.indexOf per row. TT-5478 made this map
    // iterate the full directory, so an indexOf-in-map would be O(users × loaded).
    const loadedUserIds = new Set(userIdsWithWeekSummary);

    return ids.map(userId => {
      const url = `/users/${userId}`;
      const hasSummary = loadedUserIds.has(userId.toString());
      const user = nonDeletedUsersMap[userId];

      const { id, avatar, ...rest } = user;

      // Virtualized list: LOADING if no summary yet, READY when summary loaded
      // API results (sleeping/burning): Always READY since they came from API
      const status = isVirtualizedList ? (hasSummary ? READY : LOADING) : READY;

      const wrappedUser: ItemStatusWrapper<UserViewModel> = {
        status,
        item: {
          id,
          url,
          avatar: avatar ? avatar.large_retina : '',
          summary: hasSummary
            ? userSummaryFactory(
                userCapacityEntities?.[userId],
                filter,
                accountStartOfWeek,
                accountWeeklyCapacity,
                weeksByUser[id],
                totalsByUser[id]
              )
            : undefined,
          ...rest,
        },
      };

      return wrappedUser;
    });
  }
);

export const getDashboardUsersWithWeekSummary = createSelector(
  [getDemoDashboardRows, getRealDashboardUsersWithWeekSummary],
  (demo, real) => demo ?? real
);

/**
 * The next batch of dashboard users (from the top of the sorted list) that don't
 * yet have a week summary loaded, capped to one page. Empty in sleeping/burning
 * mode, which uses the API's own page pagination. (TT-5478)
 */
export const getRealDashboardUserIdsToLoad = createSelector(
  [getUsersDashboardFilter, getDashboardDisplayUserIds, getUserIdsWithWeekSummary],
  (filter, displayUserIds, userIdsWithWeekSummary) => {
    if (filter.mode) {
      return [];
    }
    const loaded = new Set(userIdsWithWeekSummary);
    return displayUserIds.filter(id => !loaded.has(id.toString())).slice(0, SUMMARY_API_LIMIT);
  }
);

export const getDashboardUserIdsToLoad = createSelector(
  [getDemoDashboardUserIdsToLoad, getRealDashboardUserIdsToLoad],
  (demo, real) => demo ?? real
);

/**
 * Number of dashboard users (from the top of the sorted list) whose week summary
 * has loaded. Summaries load top-to-bottom, so this is the frontier past which
 * rows are still awaiting data; the list loads the next batch as it scrolls into
 * view. In sleeping/burning mode every returned row is loaded. (TT-5478)
 */
export const getRealDashboardLoadedCount = createSelector(
  [getUsersDashboardFilter, getDashboardDisplayUserIds, getUserIdsWithWeekSummary],
  (filter, displayUserIds, userIdsWithWeekSummary) => {
    if (filter.mode) {
      return displayUserIds.length;
    }
    const loaded = new Set(userIdsWithWeekSummary);
    return displayUserIds.filter(id => loaded.has(id.toString())).length;
  }
);

export const getDashboardLoadedCount = createSelector(
  [getDemoDashboardLoadedCount, getRealDashboardLoadedCount],
  (demo, real) => demo ?? real
);

/**
 * Whether more dashboard users remain to load. In the default view this is driven
 * by the directory (are there still users without a summary?), not by whether the
 * last API page was full — the latter stops early and drops users. (TT-5478)
 */
export const getRealDashboardHasMore = createSelector(
  [getUsersDashboardFilter, getHasMoreUsers, getDashboardUserIdsToLoad],
  (filter, hasMoreFromApi, idsToLoad) => (filter.mode ? hasMoreFromApi : idsToLoad.length > 0)
);

export const getDashboardHasMore = createSelector(
  [getDemoDashboardHasMore, getRealDashboardHasMore],
  (demo, real) => demo ?? real
);

// NOTE: if we want to show deleted users too we can extend this
export const getUsersForDashboardDropdown = getNonDeletedUsers;

export const getHasLoadedUsersForDropdown = readHasLoadedUsersForDropdown;

export const getShowUsersDropdown = createSelector(
  [getCurrentAccount, getNonDeletedUsers],
  (account, users) => {
    return account.num_users > 1 || users.length > 1;
  }
);

export const getRange = createSelector(
  [getUsersDashboardFilter, getStartOfWeek],
  (filter: Filter, startOfWeek: StartOfWeek) => {
    const lastWeeks = filter.period;

    return getLastWeeksRange(filter.to, lastWeeks, startOfWeek);
  }
);

export const getErrors = createSelector(
  [getDashGraphXhrUiStatus, getDashListXhrUiStatus],
  (xhrGraph, xhrList): Array<string> => {
    if (!xhrGraph.hasErrors && !xhrList.hasErrors) {
      return [];
    }

    return [...xhrGraph.errors, ...xhrList.errors].map(err => err.error);
  }
);

export const getPeriodOptions = createSelector(
  [getUsersDashboardFilter],
  (filter): Array<PeriodDropdownOption> => {
    const periodOptions = Object.keys(periodOptionsDict).map(k => {
      const option: PeriodDropdownOption = {
        action: k,
        name: periodOptionsDict[+k as keyof typeof periodOptionsDict],
        active: filter.period === +k,
        period: +k,
      };
      return option;
    });
    return periodOptions;
  }
);

export const getTimeframeFilterMethodOptions = createSelector(
  [getUsersDashboardFilter],
  (filter): Array<TimeframeFilterMethodOption> => {
    const timeframeOptions = Object.keys(timeframeFilterMethodOptionsDict).map(k => {
      const method = k as keyof typeof timeframeFilterMethodOptionsDict;
      const option: TimeframeFilterMethodOption = {
        action: k,
        name: timeframeFilterMethodOptionsDict[method],
        active: filter.timeframe_filter_method === method,
        method: method,
      };
      return option;
    });
    return timeframeOptions;
  }
);

// NOTE: code review; I use this in reducer to check incoming filter if I should reset, ok?
// Returns true only when explicitly filtered to empty arrays (no users/teams selected)
// undefined means no filter (show all users), which is different from an empty selection
export const isNoneUsersOrTeamsSelected = (filter: Filter): boolean => {
  const noUsers = Array.isArray(filter.userIds) && filter.userIds.length === 0;
  const noTeams = Array.isArray(filter.teamIds) && filter.teamIds.length === 0;
  return noUsers && noTeams;
};

/***
 * Parse the start date from a range string and pass it along to getHistoricCapacityByDate
 * @param dateRange {string} - in the format of YYYY-MM-DD-YYYY-MM-DD, representing a from & to
 * @param capacities {Array<RawCapacity>} - an array containing objects representing the user's capacity at a given time
 */
function getCapacityFromDateRange(dateRange: string, capacities: Array<RawCapacity>) {
  const dateCheck = /\d{4}-\d{2}-\d{2}/;
  const parsedFrom = dateRange.match(dateCheck)?.[0];
  if (parsedFrom) {
    return getHistoricCapacityByDate(capacities, parsedFrom);
  }
  return null;
}

const getWeekRangeFromFilter = createSelector(
  [getUsersDashboardFilter, getStartOfWeek],
  (filter, startOfWeek) => {
    const range = getLastWeeksRange(filter.to, +filter.period, startOfWeek);

    let from = fromISO(range.since);
    let to = from.plus({ days: 6 });

    const weekRange: Array<string> = [];

    for (let i = 0; i < +filter.period; i++) {
      weekRange.push(`${from.toISODate()}-${to.toISODate()}`);
      from = from.plus({ days: 7 });
      to = to.plus({ days: 7 });
    }
    return weekRange;
  }
);

export const getRealFilteredUserCapacityMap = createSelector(
  [readWeeksByUser, getUserCapacitiesEntities, getWeekRangeFromFilter],
  (weeksByUser, userCapacityEntities, weekRange) => {
    if (!userCapacityEntities) {
      return undefined;
    }

    return Object.keys(weeksByUser).reduce<Record<number, number>>((prev, userId) => {
      const currentUserWeeks: Record<string, WeekSummary> = weeksByUser[Number(userId)];
      const weeksTotal = weekRange.reduce((total, week) => {
        const currentWeek = currentUserWeeks[week];

        if (!currentWeek) {
          const userCapacity = userCapacityEntities[+userId];
          const capacityForMissingWeek = getCapacityFromDateRange(week, userCapacity);

          return capacityForMissingWeek ? total + capacityForMissingWeek.weekly_capacity : total;
        }
        return total + (currentWeek.capacity?.total_hours || 0);
      }, 0);

      return {
        ...prev,
        [userId]: weeksTotal,
      };
    }, {});
  }
);

export const getFilteredUserCapacityMap = createSelector(
  [getDemoFilteredUserCapacityMap, getRealFilteredUserCapacityMap],
  (demo, real) => demo ?? real
);

export const getRealFilteredUserChangedMap = createSelector(
  [readWeeksByUser, getUserCapacitiesEntities, getWeekRangeFromFilter],
  (weeksByUser, capacityEntities, weekRange) => {
    return Object.keys(weeksByUser).reduce(
      (prev: Record<string, { hasChanged: boolean }>, userId) => {
        const userCapacities = capacityEntities?.[+userId];
        // if the user only has one capacity entry, we can assume the checks below aren't needed
        if (userCapacities && userCapacities.length === 1) {
          return {
            ...prev,
            [userId]: { hasChanged: false },
          };
        }

        const currentUserWeeks: Record<string, WeekSummary> = weeksByUser[+userId];

        const userCapacitiesByWeek = weekRange.map(week => {
          const lookupWeek = currentUserWeeks[week];

          if (!lookupWeek && userCapacities) {
            const capacityForMissingWeek = getCapacityFromDateRange(week, userCapacities);
            if (capacityForMissingWeek) {
              return capacityForMissingWeek.weekly_capacity;
            }
          }

          return lookupWeek?.capacity?.total_hours;
        }, []);

        const capacityIsUnchanged = userCapacitiesByWeek.every(
          (capacity, i, arr) => capacity === arr[0]
        );

        return {
          ...prev,
          [userId]: { hasChanged: !capacityIsUnchanged },
        };
      },
      {}
    );
  }
);

export const getFilteredUserChangedMap = createSelector(
  [getDemoFilteredUserChangedMap, getRealFilteredUserChangedMap],
  (demo, real) => demo ?? real
);

const getIsNoneUsersOrTeamsSelected = createSelector(
  [getUsersDashboardFilter],
  (filter): boolean => {
    return isNoneUsersOrTeamsSelected(filter);
  }
);

export const getIsNotifyUserFeatureEnabled = createSelector(
  [getAccountFeatures],
  (features: Features): boolean => !!features.people_notify
);

const getDashboardUILoadingState = createSelector(
  [getDashGraphXhrUiStatus, getDashListXhrUiStatus],
  (graphXhr, userListXhr) => {
    return {
      graph: {
        isLoading: graphXhr.isLoading,
        hasInitialized: graphXhr.hasInitialized,
      },
      users: {
        isLoading: userListXhr.isLoading,
        hasInitialized: userListXhr.hasInitialized,
      },
    };
  }
);

export const getDashboardUI = createSelector(
  [
    getDashboardUILoadingState,
    getGraphData,
    getDashboardUsersWithWeekSummary,
    getErrors,
    getPeriodOptions,
    getTimeframeFilterMethodOptions,
    getUsersDashboardFilter,
    getIsNoneUsersOrTeamsSelected,
    getIsAppInitialized,
    getIsNotifyUserFeatureEnabled,
    getAccountFeatures,
    getRange,
  ],
  (
    loadingState,
    graphData,
    users,
    errors,
    periodOptions,
    timeframeFilterMethodOptions,
    filter,
    isNoneUsersOrTeamsSelected,
    isAppReady,
    isNotifyEnabled,
    features,
    { since, until }
  ) => {
    const datepickerLabel = `${DateTime.fromISO(since).toLocaleString({
      ...DateTime.DATE_MED,
      year: undefined,
    })} - ${DateTime.fromISO(until).toLocaleString({ ...DateTime.DATE_MED, year: undefined })}`;

    return {
      showEmptyGraphMessage:
        !loadingState.graph.isLoading &&
        loadingState.graph.hasInitialized &&
        (!graphData || graphData.length === 0),
      isEmptyUserList: users.length === 0 && !loadingState.users.isLoading && isAppReady,
      isLoadingUserList: loadingState.users.isLoading, // only users, not graph
      errorMessages: errors,
      periodOptions,
      selectedPeriodName: periodOptionsDict[filter.period as keyof typeof periodOptionsDict],
      timeframeFilterMethodOptions,
      selectedTimeframeFilterMethodName:
        timeframeFilterMethodOptionsDict[filter.timeframe_filter_method],
      isNotifyGroupVisible: isNotifyEnabled,
      isNotifySingleUserFeatureEnabled: isNotifyEnabled,
      isNoneUsersOrTeamsSelected,
      showGraphCapacityLine: false,
      showGraphLoadingSpinner: loadingState.graph.isLoading,
      showUserListLoadingSpinner: users.length < 1 && loadingState.users.isLoading,
      isDashboardFeatureLocked: !features.people_dashboard,
      datepickerLabel,
      isLoadingGraph: loadingState.graph.isLoading,
    };
  }
);

export const getUsersListQueryParamsFromFilter = createSelector(
  [getRange, getAccountFeatures, getFilteredUserIds, getFilteredTeamIds, getUsersDashboardFilter],
  (range, features, filteredUserIds, filteredTeamIds, filter): UsersSummaryQuery => {
    const hasAccessToTasks = !!features.tasks;

    return {
      since: range.since,
      until: range.until,
      // Only pass userIds/teamIds when they have actual filters
      userIds: filteredUserIds,
      teamIds: filteredTeamIds,
      filter: filter.mode,
      percent: filter.percent,
      timeframe_filter_method: filter.timeframe_filter_method,
      totals: false,
      groupBy: 'week',
      page: filter.page,
      perPage: filter.limit,
      hasAccessToTasks,
    };
  }
);

export const getGraphQueryParamsFromFilter = createSelector(
  [getRange, getAccountFeatures, getFilteredUserIds, getFilteredTeamIds, getUsersDashboardFilter],
  (range, features, filteredUserIds, filteredTeamIds, filter): UsersSummaryQuery => {
    const hasAccessToTasks = !!features.tasks;

    return {
      since: range.since,
      until: range.until,
      userIds: filteredUserIds,
      teamIds: filteredTeamIds,
      percent: filter.percent,
      timeframe_filter_method: filter.timeframe_filter_method,
      totals: true,
      groupBy: 'week',
      hasAccessToTasks,
    };
  }
);

const getOptions = createSelector(
  [getShowUsersDropdown, getUserId, getDashboardPreferences],
  (showUsersDropdown, currentUserId, preferences) => {
    return {
      showUsersDropdown,
      currentUserId,
      preferences,
    };
  }
);

export const getUsersDashboard = createSelector(
  [
    getUsersForDashboardDropdown,
    getTeamsAsDropdownOptions,
    getFilteredUserIds,
    getFilteredTeamIds,
    getUsersDashboardFilter,
    getCurrentAccount,
    getDashboardUsersWithWeekSummary,
    getDashboardHasMore,
    getDashboardLoadedCount,
    getGraphData,
    getUserPermissions,
    getRange,
    getDashboardUI,
    getUserWeekDetailsModalUi,
    getOptions,
  ],
  (
    allUsers,
    allTeams,
    filteredUsers,
    filteredTeams,
    filter,
    account,
    users,
    hasMore,
    loadedCount,
    graphData,
    permissions,
    range,
    ui,
    userWeekDetailsModal,
    { showUsersDropdown, currentUserId, preferences }
  ) => {
    // The full selected directory is rendered; summaries stream in by id in
    // batches. Users awaiting a summary show as skeleton rows rather than being
    // hidden, so the list matches the "Anyone" dropdown. (TT-5478)
    const usersLoaded = users;

    return {
      usersLoaded,
      allUsers,
      allTeams,
      filteredUsers,
      filteredTeams,
      showUsersDropdown,
      filter,
      account,
      hasMore,
      loadedCount,
      graphData,
      permissions,
      range,
      ui,
      userWeekDetailsModal,
      currentUserId,
      preferences,
    };
  }
);

export const getShowCapacityColumn = createSelector(
  [
    (state: IState) => getAccountFeature(state, 'weekly_user_capacity'),
    (state: IState) => state.usersPage.list.query.show,
  ],
  (hasWeeklyUserCapacityFeature, show) => {
    return hasWeeklyUserCapacityFeature && show !== 'deleted';
  }
);
